import {
    createContext, FC, useEffect,
    useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { fetchBusiness as fetchBusinessAPI } from '../../../api/business';
import {
    getOneWorkspace, getWorkspaceBookings, getWorkspaceDrivers, getWorkspaceInvitations, getWorkspaceUsers, getWorkspaceWallet,
} from '../../../api/workspaces';
import { IWallet, IWorkspace } from '../../../types/workspaces';

interface IWorkspaceDetailsContext {
    loading: boolean;
    workspace: IWorkspace | undefined;
    bookings: any;
    users: any;
    invitations: any;
    drivers: any;
    businesses: any;
    wallet: IWallet | undefined;
    setLoading: (loading: boolean) => void;
    setWorkspace: (workspace: IWorkspace) => void;
    fetchBookings: (data: any) => void;
    fetchUsers: (data?: any) => void;
    fetchInvitations: (data?: any) => void;
    fetchDrivers: (data?: any) => void;
    setWallet: (data: IWallet) => void;
    fetchBusinesses: (data?: any) => void;
}

const defaultPaginator = {
    limit: 10,
    skip: 0,
    startDate: '',
    endDate: '',
};

const defaultDataState = {
    data: [] as any[],
    count: 0,
    paginator: defaultPaginator,
    loading: false,
};

const initialContext = {
    loading: false,
    workspace: undefined,
    bookings: defaultDataState,
    users: defaultDataState,
    invitations: defaultDataState,
    drivers: defaultDataState,
    businesses: defaultDataState,
    wallet: undefined,
    setLoading: () => { },
    setWorkspace: () => { },
    fetchBookings: () => { },
    fetchUsers: () => { },
    fetchInvitations: () => { },
    fetchDrivers: () => { },
    setBusinesses: () => { },
    setWallet: () => { },
    fetchBusinesses: () => { },
};

export const WorkspaceDetailsContext = createContext<IWorkspaceDetailsContext>(initialContext);

export const WorkspaceDetailsProvider: FC = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const [workspace, setWorkspace] = useState<IWorkspace>();
    const [bookings, setBookings] = useState(defaultDataState);
    const [users, setUsers] = useState(defaultDataState);
    const [invitations, setInvitations] = useState(defaultDataState);
    const [drivers, setDrivers] = useState(defaultDataState);
    const [businesses, setBusinesses] = useState(defaultDataState);
    const [wallet, setWallet] = useState<IWallet>();
    const { id } = useParams();

    const fetchBookings = async (paginator = defaultPaginator) => {
        try {
            setBookings({ ...bookings, loading: true });
            const { data } = await getWorkspaceBookings(id as string, paginator);
            setBookings({
                paginator,
                data: data.data,
                count: data.meta.itemCount,
                loading: false,
            });
        } catch (error) {
            setBookings({ ...bookings, loading: false });
            console.log(error);
        }
    };

    const fetchUsers = async (paginator = defaultPaginator) => {
        try {
            setUsers({ ...users, loading: true });
            const { data: { data, meta } } = await getWorkspaceUsers(id as string, paginator);
            setUsers({
                paginator,
                data,
                count: meta.itemCount,
                loading: false,
            });
        } catch (error) {
            setUsers({ ...users, loading: false });
            console.log(error);
        }
    };

    const fetchInvitations = async (paginator = defaultPaginator) => {
        try {
            setInvitations({ ...invitations, loading: true });
            const { data: { data, meta } } = await getWorkspaceInvitations(id as string, paginator);
            setInvitations({
                paginator,
                data,
                count: meta.itemCount,
                loading: false,
            });
        } catch (error) {
            setInvitations({ ...invitations, loading: false });
            console.log(error);
        }
    };

    const fetchDrivers = async (paginator = defaultPaginator) => {
        try {
            setDrivers({ ...drivers, loading: true });
            const { data: { data, meta } } = await getWorkspaceDrivers(id as string, paginator);
            setDrivers({
                paginator,
                data,
                count: meta.itemCount,
                loading: false,
            });
        } catch (error) {
            setDrivers({ ...drivers, loading: false });
            console.log(error);
        }
    };

    const fetchBusinesses = async (paginator = defaultPaginator) => {
        setBusinesses((prevState) => ({ ...prevState, loading: true }));
        try {
            const page = Math.floor((paginator.skip || 0) / paginator.limit) + 1;
            const { data } = await fetchBusinessAPI({
                limit: paginator.limit,
                page,
                workspaceID: id as string,
            });
            const rows = (data?.data || []).map((item: any) => ({
                _id: item?.userID?._id,
                userUniqueID: item?.userID?.userUniqueID,
                name: item?.userID?.name,
                lastName: item?.userID?.lastName,
                email: item?.userID?.email,
                companyName: item?.userID?.businessID?.companyName,
                status: item?.userID?.businessID?.status,
                createdAt: item?.userID?.businessID?.createdAt,
                externalAPIConfiguration: item?.userID?.externalAPIConfiguration || { deliveryProviders: [] },
            }));
            setBusinesses({
                paginator,
                data: rows,
                count: data?.meta?.itemCount || 0,
                loading: false,
            });
        } catch (error) {
            setBusinesses((prevState) => ({ ...prevState, loading: false }));
            console.log(error);
        }
    };

    const fetchWallet = async () => {
        if (!id) return;
        setWallet(wallet);
        try {
            const { data } = await getWorkspaceWallet(id);
            setWallet(data.data);
        } catch (error) {
            setWallet(wallet);
            console.log(error);
        }
    };

    useEffect(() => {
        if (id) {
            setLoading(true);
            getOneWorkspace(id)
                .then(({ data }) => {
                    setWorkspace(data.data);
                    fetchBookings();
                    fetchWallet();
                })
                .catch((err) => console.log(err))
                .finally(() => setLoading(false));
        }
    }, [id]);

    return (
        <WorkspaceDetailsContext.Provider
            value={{
                loading,
                workspace,
                bookings,
                users,
                invitations,
                drivers,
                businesses,
                wallet,
                setLoading,
                setWorkspace,
                fetchBookings,
                fetchUsers,
                fetchInvitations,
                fetchDrivers,
                setWallet,
                fetchBusinesses,
            }}
        >
            {children}
        </WorkspaceDetailsContext.Provider>
    );
};
